<template>
  <div id="app">
    <nav class="navbar navbar-expand-lg klinger-header">
      <div class="app-container d-flex justify-content-between">
        <router-link :to="homeRoute" class="navbar-brand">
          <img src="@/assets/klinger-logo.svg" alt="Klinger Nederland" class="px-1" height="40">
        </router-link>
        
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span class="navbar-toggler-icon"></span>
        </button>
        
        <div class="collapse navbar-collapse" id="navbarNav">
          <ul class="navbar-nav ms-auto klinger-nav">
            <!-- For managers/directors: Goedkeuringen first -->
            <template v-if="isAuthenticated && canApprove">
              <li class="nav-item">
                <router-link to="/approvals" class="nav-link" :class="{ 'active': isActiveRoute('/approvals') }">
                  Goedkeuringen
                  <span class="badge bg-danger ms-1" v-if="pendingApprovals > 0">
                  {{ pendingApprovals }}
                </span>
              </router-link>
            </li>
              <li class="nav-item">
                <router-link to="/k-numbers" class="nav-link" :class="{ 'active': isActiveRoute('/k-numbers') }">
                  K-Nummers
                </router-link>
              </li>
            </template>
            <!-- For requesters: only K-Nummers -->
            <template v-else-if="isAuthenticated">
              <li class="nav-item">
                <router-link to="/k-numbers" class="nav-link" :class="{ 'active': isActiveRoute('/k-numbers') }">
                  K-Nummers
                </router-link>
              </li>
            </template>
            
            <li class="nav-item" v-if="isAuthenticated && isAdmin">
              <router-link to="/admin" class="nav-link" :class="{ 'active': isActiveRoute('/admin') }">
                Beheer
              </router-link>
            </li>
            
            <!-- Sync indicator -->
            <li class="nav-item d-flex align-items-center me-3" v-if="isSyncing">
              <span class="sync-indicator">
                <span class="spinner-border spinner-border-sm text-muted me-1"></span>
                <small class="text-muted">Synchroniseren...</small>
              </span>
            </li>
            
            <li class="nav-item dropdown" v-if="isAuthenticated">
              <a class="nav-link dropdown-toggle" href="#" data-bs-toggle="dropdown">
                {{ userName }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end">
                <li><div class="dropdown-header">
                  <div class="fw-bold">{{ userName }}</div>
                  <small class="text-muted">{{ userEmail }}</small>
                  <div class="badge bg-secondary mt-1">{{ userRole }}</div>
                </div></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/profile" class="dropdown-item">
                  <i class="bi bi-person me-2"></i>Profiel
                </router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><a href="#" @click="logout" class="dropdown-item">
                  <i class="bi bi-box-arrow-right me-2"></i>Uitloggen
                </a></li>
              </ul>
            </li>
            <li class="nav-item" v-else>
              <router-link to="/login" class="nav-link">
                Inloggen
              </router-link>
            </li>
          </ul>
        </div>
      </div>
      <span class="version-number">v{{ version }}</span>
    </nav>
    
    <main class="app-container py-4">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    
    <footer class="klinger-footer mt-auto py-3 bg-light">
      <div class="app-container text-center text-muted">
        <img src="@/assets/klinger-logo.svg" alt="Klinger Nederland" height="30" class="mb-2 d-block mx-auto opacity-50">
        <small>&copy; 2024 Klinger Nederland. Alle rechten voorbehouden.</small>
      </div>
    </footer>

    <!-- Role Changed Modal - No way to close without reloading -->
    <div v-if="showRoleChangedModal" class="role-changed-overlay">
      <div class="role-changed-modal">
        <div class="role-changed-icon">
          <i class="bi bi-exclamation-triangle-fill"></i>
        </div>
        <h4>Je rol is gewijzigd</h4>
        <p class="text-muted">
          Je rol is gewijzigd door een beheerder.<br>
          <strong>{{ roleChangeInfo.oldRole }}</strong> → <strong>{{ roleChangeInfo.newRole }}</strong>
        </p>
        <p class="small text-muted">
          Klik op herladen om verder te gaan met je nieuwe rol.
        </p>
        <button @click="reloadPage" class="btn btn-primary btn-lg">
          <i class="bi bi-arrow-clockwise me-2"></i>Herladen
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, provide, onMounted, onUnmounted, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import socketService from '@/services/socket'
import packageInfo from '../package.json'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Version from package.json
const version = packageInfo.version

// Role changed modal state
const showRoleChangedModal = ref(false)
const roleChangeInfo = reactive({
  oldRole: '',
  newRole: ''
})

const roleLabelsMap = {
  'administrator': 'Beheerder',
  'director': 'Director',
  'manager': 'Manager',
  'requester': 'Aanvrager'
}

// Handle role changed event from socket
const handleRoleChanged = (data) => {
  roleChangeInfo.oldRole = roleLabelsMap[data.oldRole] || data.oldRole
  roleChangeInfo.newRole = roleLabelsMap[data.newRole] || data.newRole
  showRoleChangedModal.value = true
}

const reloadPage = () => {
  window.location.reload()
}

// Handle K-number events to refresh pending approvals count
const handleKNumberChange = () => {
  // Refresh pending approvals count when any K-number changes
  if (canApprove.value) {
    authStore.fetchPendingApprovals()
  }
}

// Track if listener is registered
let listenerRegistered = false

const setupSocketListener = () => {
  if (authStore.isAuthenticated && !listenerRegistered) {
    socketService.connect()
    socketService.authenticateSocket()
    socketService.on('user:roleChanged', handleRoleChanged)
    // Listen for K-number changes to update navbar badge
    socketService.on('kNumber:created', handleKNumberChange)
    socketService.on('kNumber:updated', handleKNumberChange)
    socketService.on('kNumber:deleted', handleKNumberChange)
    listenerRegistered = true
  }
}

const cleanupSocketListener = () => {
  if (listenerRegistered) {
    socketService.off('user:roleChanged', handleRoleChanged)
    socketService.off('kNumber:created', handleKNumberChange)
    socketService.off('kNumber:updated', handleKNumberChange)
    socketService.off('kNumber:deleted', handleKNumberChange)
    listenerRegistered = false
  }
}

// Watch for authentication changes
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      setupSocketListener()
    } else {
      cleanupSocketListener()
    }
  },
  { immediate: true }
)

// Also setup on mount if already authenticated
onMounted(() => {
  setupSocketListener()
})

onUnmounted(() => {
  cleanupSocketListener()
})

// Sync indicator state - provided globally for socket updates
const isSyncing = ref(false)
let syncTimeout = null

const setSyncing = (value) => {
  if (value) {
    isSyncing.value = true
    // Auto-hide after 2 seconds
    if (syncTimeout) clearTimeout(syncTimeout)
    syncTimeout = setTimeout(() => {
      isSyncing.value = false
    }, 2000)
  } else {
    isSyncing.value = false
  }
}

// Provide sync function globally
provide('setSyncing', setSyncing)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const userName = computed(() => authStore.user?.firstName + ' ' + authStore.user?.lastName || 'Gebruiker')
const userEmail = computed(() => authStore.user?.email || '')
const userRole = computed(() => {
  const roleLabels = {
    'administrator': 'Beheerder',
    'director': 'Director',
    'manager': 'Manager',
    'requester': 'Aanvrager'
  }
  return roleLabels[authStore.user?.role] || authStore.user?.role || ''
})
const canApprove = computed(() => ['manager', 'director', 'administrator'].includes(authStore.user?.role))
const isAdmin = computed(() => authStore.user?.role === 'administrator')
const pendingApprovals = computed(() => authStore.pendingApprovals || 0)

// Home route based on role
const homeRoute = computed(() => {
  if (canApprove.value) return '/approvals'
  return '/k-numbers'
})

// Check if route is active (for underline styling)
const isActiveRoute = (path) => {
  const currentPath = router.currentRoute.value.path
  if (path === '/k-numbers') {
    return currentPath.startsWith('/k-numbers')
  }
  if (path === '/admin') {
    return currentPath.startsWith('/admin')
  }
  return currentPath === path
}

const logout = async () => {
  try {
    await authStore.logout()
    toast.success('Succesvol uitgelogd')
    router.push('/login')
  } catch (error) {
    toast.error('Fout bij uitloggen')
  }
}
</script>

<style scoped>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Navbar positioning for version number */
.navbar {
  position: relative;
}

main {
  flex: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  margin: 0.25rem;
}

.nav-link.dropdown-toggle::after {
  margin-left: auto;
}

/* Active nav link underline */
.nav-link.active {
  position: relative;
  font-weight: 600;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0.5rem;
  right: 0.5rem;
  height: 2px;
  background-color: #003a68;
  border-radius: 2px 2px 0 0;
}

/* Sync indicator */
.sync-indicator {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
}

/* Role changed modal - blocking overlay */
.role-changed-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.role-changed-modal {
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalAppear 0.3s ease-out;
}

@keyframes modalAppear {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.role-changed-icon {
  font-size: 3rem;
  color: #fd7e14;
  margin-bottom: 1rem;
}

.role-changed-modal h4 {
  margin-bottom: 1rem;
  color: #333;
}

.role-changed-modal p {
  margin-bottom: 1.5rem;
}

.role-changed-modal .btn {
  min-width: 150px;
}

/* Version number in navbar - right side like eportal */
.version-number {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.3);
  position: absolute;
  right: 0.5rem;
  bottom: 0.25rem;
}
</style>
